import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useFeatureContext } from '../../../contexts/FeatureProvider';
import useFeatureActions from '../../../hooks/APIHooks/users/useFeatureActions';
import { formatFeatureName } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';

const FeatureModalContainer = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    targetedFeature,
    showDeleteModal,
    setTargetedFeature,
    setSearchText,
    setShowDeleteModal,
  } = useFeatureContext();

  // integration of custom API hooks here
  const { deleteFeatureHandler } = useFeatureActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle deleting feature declared here
  const deleteExistingFeatureHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteFeatureHandler
        .mutateAsync({ featureId: targetedFeature?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Feature Deleted!!');

            setTargetedFeature(null);
            setShowDeleteModal(false);
            setSearchText('');
            queryClient.invalidateQueries({ queryKey: ['feature-list'] });
          } else {
            toast.error(res.data.message || 'Feature Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering feature modal container component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to remove this{' '}
                <span className='font-semibold text-base-light-blue'>
                  ({formatFeatureName(targetedFeature?.name as string)})
                </span>{' '}
                feature? Deleted feature can be added again in future if needed.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteExistingFeatureHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default FeatureModalContainer;
